// Route guard for `/org/scenarios/:scenarioId` — the same gate the /org tab strip
// applies to the scenarios tab (`hris.edit`, the cell every scenario MUTATION
// route's preHandler requires).
//
// WHY IT EXISTS. org-content.tsx withholds the scenarios TAB from a caller who
// only reads the org, but the DETAIL route sat behind `RequireAuth` alone — so
// the surface the tab withheld was one typed URL away, and the notification /
// task deep links hand that URL out. Two entrances to one screen must not
// disagree about who may use it.
//
// ⚠ THE CLIENT IS NOT A TRUST BOUNDARY. Anyone can skip a React guard; what
// this guard buys is consistency — the product stops showing a surface it
// already decided not to offer.
//
// THE SERVER HOLE THIS COMMENT USED TO DESCRIBE IS CLOSED, AND THE SERVER NOW
// ADMITS PEOPLE THIS GUARD TURNS AWAY. Both scenario read routes narrow rows
// via `resolveScenarioReadVisibility` (services/platform-graph/src/routes/org.ts,
// pinned by org-scenario-read-scope.integration.test.ts): company-scoped tier
// OR the scenario's author OR anyone named in the linked approval chain.
// `SCENARIO_MUTATE_CELL` is none of those three — it is the MUTATION cell, and
// the seeded GM and manager templates do not carry it. So a reorg APPROVER who
// follows their notification deep-link here is bounced to /org by this guard
// even though the server would hand them the diff they are being asked to
// approve. Widening the gate is a product decision (which cell, or a
// server-supplied per-scenario capability) and is not this file's to invent.

import { useTranslation } from "react-i18next";
import { Navigate, Outlet } from "react-router";

import { Loading } from "@novemai/components";

import { useEffectivePermissions } from "@/features/roles-permissions/use-effective-permissions";

import { SCENARIO_MUTATE_CELL } from "./scenario-actions";

export function RequireScenarioAccess(): React.ReactElement {
  const { t } = useTranslation();
  const { capabilities, isLoading } = useEffectivePermissions();

  // Default-closed WITHOUT flashing a redirect: the capability Set is empty
  // while the query is in flight, so deciding before it settles would bounce
  // every legitimate deep-link arrival back to the chart. On ERROR the set
  // stays empty and the redirect below fires — same fail-closed answer the tab
  // gives, not an open door.
  if (isLoading) return <Loading label={t("org_scenarios.loading")} rows={4} />;

  if (!capabilities.has(SCENARIO_MUTATE_CELL)) return <Navigate to="/org" replace />;

  return <Outlet />;
}
